import { nanoid } from 'nanoid';
import { getPreviewUrl } from './utils';

const PREVIEW_NOT_READY_MARKERS = ['Preview Not Yet Generated', '棰勮灏氭湭鐢熸垚'];

const buildCacheBustedPreviewUrl = (url: string) =>
  `${url}${url.includes('?') ? '&' : '?'}t=${nanoid()}`;

const canReadPreviewHtmlInBrowser = (previewUrl: string) => {
  if (typeof window === 'undefined') return false;

  try {
    const resolvedUrl = new URL(previewUrl, window.location.href);
    return resolvedUrl.origin === window.location.origin;
  } catch {
    return false;
  }
};

const fetchPreviewHtml = async (previewUrl: string) => {
  const requestUrl = buildCacheBustedPreviewUrl(previewUrl);
  const response = await fetch(requestUrl, {
    mode: 'cors',
    cache: 'no-store',
  });

  if (!response.ok) {
    return null;
  }

  const html = await response.text();
  const isHtml = html.includes('<html') || html.includes('<!DOCTYPE');
  const isNotReady = PREVIEW_NOT_READY_MARKERS.some((marker) => html.includes(marker));

  if (!isHtml || isNotReady) {
    return null;
  }

  return {
    html,
    requestUrl,
  };
};

export async function checkPreviewIsReady(projectId: string | null): Promise<string | null> {
  const previewUrl = getPreviewUrl(projectId);

  try {
    const result = await fetchPreviewHtml(previewUrl);
    return result?.requestUrl || null;
  } catch (error) {
    console.warn('[checkPreviewIsReady] Preview is not ready yet:', error);
    return null;
  }
}

export async function loadPermanentPreviewHtml(
  previewUrl: string,
  options: { allowCrossOrigin?: boolean } = {},
): Promise<{
  html: string;
  previewUrl: string;
} | null> {
  try {
    if (!previewUrl) return null;
    if (!options.allowCrossOrigin && !canReadPreviewHtmlInBrowser(previewUrl)) return null;

    const result = await fetchPreviewHtml(previewUrl);
    if (!result) return null;

    return {
      html: result.html,
      previewUrl,
    };
  } catch (error) {
    console.warn('[loadPermanentPreviewHtml] Failed to load permanent preview HTML:', error);
    return null;
  }
}

export async function pollPreviewReady(
  projectId: string,
  onReady: (url: string) => void,
  onFail?: (errorMsg: string) => void,
  maxRetries = 15,
  interval = 2000,
) {
  let retryCount = 0;

  const poll = async () => {
    const readyUrl = await checkPreviewIsReady(projectId);

    if (readyUrl) {
      onReady(readyUrl);
      return;
    }

    retryCount += 1;
    if (retryCount < maxRetries) {
      setTimeout(poll, interval);
    } else {
      const errorMsg = '[pollPreviewReady] Preview page readiness check timed out.';
      console.error(errorMsg);
      onFail?.(errorMsg);
    }
  };

  poll();
}
